import type { Difficulty, MCQQuestion } from './types';

// Mulberry32 — small deterministic PRNG seeded by integer
function mulberry32(seed: number): () => number {
  let state = seed | 0;
  return function () {
    state = (state + 0x6d2b79f5) | 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function randInt(rand: () => number, min: number, max: number): number {
  return Math.floor(rand() * (max - min + 1)) + min;
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/**
 * N-3-17：時鐘與時刻讀取——三下第七章。
 * 重點在整點/半點/15分的鐘面讀法，區別「時刻」與「時間長度」。
 *
 * difficulty 1：整點（XX:00）與半點（XX:30）
 * difficulty 2：15 分鐘間隔（XX:00/15/30/45）
 * difficulty 3：5 分鐘間隔（任意 XX:05/10…55）
 */
export function generateTimeClockReading(seed: number, difficulty: Difficulty): MCQQuestion {
  const rand = mulberry32(seed);

  const hour = randInt(rand, 1, 12);

  let minute: number;
  let minuteChoices: number[];

  if (difficulty === 1) {
    minuteChoices = [0, 30];
  } else if (difficulty === 2) {
    minuteChoices = [0, 15, 30, 45];
  } else {
    minuteChoices = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];
  }

  minute = minuteChoices[randInt(rand, 0, minuteChoices.length - 1)]!;

  const correctStr = `${hour}:${pad2(minute)}`;

  // Build distractors
  const distractors: string[] = [];
  const variants = [
    `${hour === 12 ? 1 : hour + 1}:${pad2(minute)}`,
    `${hour === 1 ? 12 : hour - 1}:${pad2(minute)}`,
    `${hour}:${pad2((minute + 15) % 60)}`,
    `${hour}:${pad2((minute + 45) % 60)}`,
    `${hour}:${pad2((minute + 5) % 60)}`,
    `${hour}:${pad2((minute + 55) % 60)}`,
  ];

  for (const v of variants) {
    if (v !== correctStr && !distractors.includes(v) && distractors.length < 3) {
      distractors.push(v);
    }
  }
  while (distractors.length < 3) {
    const h = randInt(rand, 1, 12);
    const m = minuteChoices[randInt(rand, 0, minuteChoices.length - 1)]!;
    const s = `${h}:${pad2(m)}`;
    if (s !== correctStr && !distractors.includes(s)) distractors.push(s);
  }

  const allOpts = [correctStr, ...distractors.slice(0, 3)];
  const shuffled = [...allOpts].sort(
    (x, y) => mulberry32(seed + x.charCodeAt(0) * 13)() - mulberry32(seed + y.charCodeAt(0) * 13)(),
  );
  const correctIdx = shuffled.indexOf(correctStr);

  const minuteDesc =
    minute === 0  ? '整' :
    minute === 30 ? '半點（三十分）' :
    `${minute} 分`;

  const ampm = hour <= 6 ? '早上' : hour <= 11 ? '上午' : hour === 12 ? '中午' : '下午';

  return {
    kind: 'mcq',
    prompt: `鐘面上，時針指向 ${hour}，分針指向 ${minute === 0 ? 12 : minute === 30 ? 6 : minute / 5}，這是${ampm}幾時${minuteDesc}？`,
    options: shuffled,
    correctAnswer: correctIdx,
    explanation: `時針在 ${hour}，分針在 ${minute === 0 ? 12 : minute === 30 ? 6 : minute / 5}（每格 5 分），所以是 ${correctStr}。`,
    kp: 'math.time.clock',
  };
}
